/**
 * Easter paper crafts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterPaperCraftsPack: ToilePack = {
    slug: 'easter-paper-crafts',
    category: 'season',
    name: 'Easter paper crafts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'basket', label: 'Paper basket', subject: 'A template sheet for a fold-up paper basket net with a handle strip, fold lines and tabs marked'},
        {key: 'ears', label: 'Bunny ears', subject: 'A template sheet for a bunny ears headband with a band strip and two ears, dashed cut lines marked'},
        {key: 'card', label: 'Egg card', subject: 'A template sheet for a fold-over egg-shaped card with a pattern to colour, the fold line marked'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'pastel easter printable, egg and floral borders, soft rounded lettering, print-ready', palette: 'Soft pink, mint, lilac and butter yellow', framing: 'Laid out on a vertical page with an even white margin'},
        {key: 'vintage', label: 'Vintage', style: 'vintage easter printable, engraved floral borders, elegant serif lettering, print-ready', palette: 'Rose, sage, cream and a little gold', framing: 'Laid out on a cream page with an ornate border and a margin'},
        {key: 'kids', label: 'Kids', style: "kids' easter printable, bright cartoon bunnies and chicks, rounded lettering, print-ready", palette: 'Bright yellow, sky blue, grass green and pink', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['a chick instead of a bunny', 'for adult-sized paper crowns', 'with a simpler two-fold version'],
    directionHint: 'try: a chick instead of a bunny',
    seo: {
        title: 'Easter Paper Crafts: Basket, Ears & Card Templates',
        metaDescription: 'Nine Easter paper-craft templates — a fold-up basket, bunny ears headband, and egg card — fold and cut lines drawn, ready to print and make.',
        h1: 'Easter paper crafts: basket, ears, and card templates',
        tagline: 'Nine templates, three crafts, three styles each — fold and cut lines drawn, print-ready.',
        intro: "Three Easter paper-craft templates — a fold-up basket, a bunny-ears headband, and an egg-shaped card — each with fold and cut lines marked, in pastel, vintage, and kids' cartoon styles. Ask for a different craft, a bigger size for adults, or a matching gift tag, and the same template grammar carries it.",
        useCases: [
            'a classroom Easter craft session',
            'a birthday-party bunny headband',
            'a handmade Easter card',
            'a basket for the egg hunt',
        ],
        faq: [
            {q: 'Do the fold and cut lines actually print on the page?', a: "Yes — every fold line is labeled small 'FOLD' and every cut line 'CUT' directly on the template."},
            {q: 'What size do these print at?', a: 'Each template is a single vertical page; scale it at your printer for a bigger adult-sized version.'},
            {q: 'Can I get a different craft or a bigger size?', a: 'Yes — the direction field takes any variation, including a different craft or a resized template.'},
            {q: 'What are the three styles?', a: "Pastel, vintage engraved-border, and bright kids' cartoon."},
        ],
    },
}
